// User-profile binding. Mirrors backend/app/schemas/profile.py + /profile/*.
// Used by the web Settings page for generation language + explanation mode.
import { api, API_BASE_URL, API_PREFIX, ApiError } from "./client";
import { tokenStore } from "./token-store";

export interface Profile {
  user_id: string;
  first_name: string | null;
  preferred_language: string;
  generation_language: string | null;
  // language-trainer explanation mode: "immersion" | "native"
  explanation_mode: string | null;
  // Onboarding "About you" fields (mirror iOS).
  learning_status: string | null;
  subject_interests: string[];
  difficulty_preference: string | null;
  country: string | null;
  [k: string]: unknown;
}

export interface AvatarResponse {
  avatar_url: string | null;
}

export interface ProfileUpdate {
  first_name?: string | null;
  preferred_language?: string;
  generation_language?: string | null;
  explanation_mode?: string | null;
  learning_status?: string | null;
  subject_interests?: string[];
  difficulty_preference?: string | null;
  country?: string | null;
  [k: string]: unknown;
}

export function getProfile(): Promise<Profile> {
  return api.get<Profile>("/profile/");
}

export function updateProfile(patch: ProfileUpdate): Promise<Profile> {
  return api.patch<Profile>("/profile/", patch);
}

/** Create the profile row (POST). A fresh account has none — the backend 409s if
 *  one already exists, so onboarding falls back to {@link updateProfile}. */
export function createProfile(payload: ProfileUpdate): Promise<Profile> {
  return api.post<Profile>("/profile/", payload);
}

/** Save the onboarding "About you" fields — POST first (fresh account), falling
 *  back to PATCH on 409 (profile already exists). Mirrors iOS persistProgress(). */
export async function saveOnboardingProfile(payload: ProfileUpdate): Promise<Profile> {
  try {
    return await createProfile(payload);
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) return updateProfile(payload);
    throw err;
  }
}

/** Upload (replace) the caller's profile picture. Multipart, mirroring
 *  api/files.ts (the shared client is JSON-only). Returns the presigned URL. */
export async function uploadAvatar(file: File): Promise<AvatarResponse> {
  if (file.size === 0) throw new ApiError(422, "That image is empty — try again.");
  const form = new FormData();
  form.append("file", file, file.name);

  const headers: Record<string, string> = { Accept: "application/json" };
  const token = tokenStore.access;
  if (token) headers.Authorization = `Bearer ${token}`;
  // Do NOT set Content-Type — the browser sets the multipart boundary.

  const res = await fetch(`${API_BASE_URL}${API_PREFIX}/profile/picture`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") message = data.detail;
    } catch {
      /* keep generic */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as AvatarResponse;
}

export function deleteAvatar(): Promise<AvatarResponse> {
  return api.delete<AvatarResponse>("/profile/picture");
}
